import type { AccessibleLocation, CanonicalLocationAddress } from '../types/access'

// Canonical worksite-address helpers for location DTOs.
//
// Modern backend DTOs carry `address` as the canonical object plus a readable
// `displayAddress` string. Older cached contexts and mocked fixtures may still
// carry `address` as a plain string or only `metadata.address`; these helpers
// absorb every historical shape so components never parse metadata directly.

type LooseLocation = Partial<AccessibleLocation> & { metadata?: Record<string, unknown> | null }

const text = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

const finiteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const addressObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null

export function normalizeLocationAddress(location: LooseLocation | null | undefined): CanonicalLocationAddress {
  const metadata = (location?.metadata || {}) as Record<string, unknown>
  const fromDto = addressObject(location?.address)
  const metadataAddress = metadata.address
  const fromMetadataObject = addressObject(metadataAddress)
  const legacyString = typeof location?.address === 'string'
    ? text(location.address)
    : typeof metadataAddress === 'string' ? text(metadataAddress) : ''

  const pick = (...values: unknown[]): string | null => {
    for (const value of values) {
      const trimmed = text(value)
      if (trimmed) return trimmed
    }
    return null
  }

  const line1 = pick(
    fromDto?.line1, location?.addressLine1, fromMetadataObject?.line1,
    metadata.addressLine1, metadata.address_line1, legacyString
  )
  const latitude = finiteNumber(fromDto?.latitude ?? location?.latitude ?? fromMetadataObject?.latitude ?? metadata.latitude ?? metadata.lat)
  const longitude = finiteNumber(fromDto?.longitude ?? location?.longitude ?? fromMetadataObject?.longitude ?? metadata.longitude ?? metadata.lng ?? metadata.lon)
  const bothValid = latitude !== null && longitude !== null

  return {
    line1,
    line2: pick(fromDto?.line2, location?.addressLine2, fromMetadataObject?.line2),
    city: pick(fromDto?.city, location?.city, fromMetadataObject?.city, metadata.city),
    region: pick(
      fromDto?.region, location?.stateProvinceRegion, fromMetadataObject?.region,
      metadata.region, metadata.state, metadata.province, metadata.parish
    ),
    postalCode: pick(fromDto?.postalCode, location?.postalCode, fromMetadataObject?.postalCode, metadata.postalCode, metadata.postal_code),
    countryCode: pick(fromDto?.countryCode, location?.countryCode, fromMetadataObject?.countryCode, metadata.countryCode, metadata.country_code)?.toUpperCase() || null,
    formattedAddress: pick(
      fromDto?.formattedAddress, location?.displayAddress, fromMetadataObject?.formattedAddress,
      metadata.formatted_address, legacyString
    ),
    latitude: bothValid ? latitude : null,
    longitude: bothValid ? longitude : null,
    resolutionSource: pick(fromDto?.resolutionSource),
    resolvedAt: pick(fromDto?.resolvedAt),
  }
}

/** One readable line for tables, labels, and drawers. */
export function locationDisplayAddress(location: LooseLocation | null | undefined): string {
  const address = normalizeLocationAddress(location)
  return address.formattedAddress
    || [address.line1, address.line2, address.city, address.region, address.postalCode, address.countryCode].filter(Boolean).join(', ')
}

export function hasResolvedCoordinates(address: Pick<CanonicalLocationAddress, 'latitude' | 'longitude'>): boolean {
  return Number.isFinite(address.latitude as number) && Number.isFinite(address.longitude as number)
}
